import { ApplicationRef, effect } from '@angular/core';
import { ConfigService } from '@rxap/config';
import { Environment } from '@rxap/environment';
import { PubSubService, RXAP_TOPICS } from '@rxap/ngx-pub-sub';
import posthog, { PostHogConfig } from 'posthog-js';
import { tap } from 'rxjs';
import { confidentialityStore } from './confidentiality/confidentiality.store';
import { PrivacyConsentService } from './privacy-consent/privacy-consent.service';

/**
 * Initializes the PostHog analytics service for the Angular application.
 *
 * This function is intended to be used as an `APP_INITIALIZER` factory. It performs
 * several checks to determine if PostHog should be initialized:
 *
 * 1.  It checks for a `posthog` configuration object from the `ConfigService`.
 * 2.  It checks if the configuration has a `disabled: true` flag.
 * 3.  It checks if the `environment` (from `ConfigService`) is 'development'.
 * 4.  It checks if the `Environment` object's `production` flag is `false`.
 *
 * If none of these conditions are met, it initializes PostHog with the provided
 * key and configuration. It also subscribes to the `RXAP_TOPICS.authentication.logout`
 * event to reset PostHog analytics when a user logs out.
 *
 * @param {ApplicationRef} app - The Angular `ApplicationRef`, used to access the app's injector.
 * @param {ConfigService} configService - The application's central configuration service.
 * @param {any} logger - The logger service (type is 'any', as specified in the original function).
 * @param {any} options - Additional options (type is 'any', as specified in the original function).
 * @param {Environment} environment - The Angular `Environment` object (from `@rxap/environment`).
 */
export function initPosthog(
  app: ApplicationRef,
  configService: ConfigService,
  logger: any,
  options: any,
  environment: Environment
) {
  const privacyConsentService = app.injector.get(PrivacyConsentService);
  const consent = privacyConsentService.consent();

  const posthogConfig = configService.get<
    { key: string; disabled?: boolean } & PostHogConfig
  >('posthog');

  if (!posthogConfig) {
    console.warn('posthog configuration not found');
    return;
  }

  const { key, disabled, ...config } = posthogConfig;

  if (disabled) {
    console.warn('posthog is disabled');
    return;
  }

  if (configService.get('environment') === 'development') {
    console.warn('posthog is disabled in environment = development');
    return;
  }

  if (!environment.production) {
    console.warn('posthog is disabled. Is only enabled in production mode');
    return;
  }

  console.log('initialize posthog');

  try {
    posthog.init(key, {
      ...config,
      // If no consent or posthog is not explicitly allowed, start opted-out
      opt_out_capturing_by_default: !consent?.posthog,
    });
  } catch (e) {
    console.error('Failed to initialize posthog', e);
    return;
  }

  // Subscribe to logout events to reset PostHog analytics for the user
  app.injector
    .get(PubSubService)
    .subscribe(RXAP_TOPICS.authentication.logout)
    .pipe(tap(() => posthog.reset()))
    .subscribe();

  // Associate PostHog events with the authenticated user. The user info is
  // populated by the oauth2-proxy "confidentiality" flow (`/oauth2/auth` /
  // `/oauth2/userinfo`) and exposed via the confidentiality store. The effect
  // reads both the consent and the user info signals, so it (re-)identifies
  // whenever either becomes available — covering late sign-in and post-init
  // consent opt-in. Gating on consent avoids emitting an $identify that the
  // opted-out client would drop anyway.
  const store = app.injector.get(confidentialityStore);

  effect(
    () => {
      const currentConsent = privacyConsentService.consent();
      const userInfo = store.userInfo();
      if (currentConsent?.posthog && userInfo?.email) {
        posthog.identify(userInfo.email, { ...userInfo });
      }
    },
    { injector: app.injector }
  );
}
